import { FunctionalAsset } from '@synergy/models';
import { createSelector } from 'reselect';
import dayjs from 'dayjs';

import { AppState } from '../../store/store';
import { DeliveryOptions } from '../models/delivery-options';
import { ShoppingCartState } from './shopping-cart-reducer';
import { currencySelector, localesSelector } from './configuration-selectors';
import { mapDeliveryOptions } from '../models/mapper/delivery-options-mapper';
import { mapShoppingCart } from '../models/mapper/shopping-cart-mapper';

const shoppingCartState = (state: AppState): ShoppingCartState => state.shoppingCart;
type S = [typeof shoppingCartState]; // Just a helper type to get type safety using createSelector

export const shoppingCartSelector = createSelector(
    currencySelector,
    localesSelector,
    shoppingCartState,
    (currency, locales, state) => (!state.shoppingCart ? null : mapShoppingCart(state.shoppingCart, locales, currency))
);

export const deliveryOptionSelector = createSelector<S, DeliveryOptions | null>(shoppingCartState, state =>
    state.deliveryOptions ? mapDeliveryOptions(state.deliveryOptions) : null
);

export const deliveryAvisoContactsSelector = createSelector(deliveryOptionSelector, options => {
    const avisoContacts = options ? [...options.avisoContacts] : [];

    return avisoContacts?.sort((a, b) => a.firstName.localeCompare(b.firstName)) ?? [];
});

export const deliveryAddressesSelector = createSelector(deliveryOptionSelector, options => {
    const deliveryAddresses = options ? [...options.deliveryAddresses] : [];

    return deliveryAddresses?.sort((a, b) => a.zip.localeCompare(b.zip)) ?? [];
});

export const invoiceAddressesSelector = createSelector(deliveryOptionSelector, options => {
    const invoiceAddresses = options ? [...options.invoiceAddresses] : [];

    return invoiceAddresses?.sort((a, b) => a.zip.localeCompare(b.zip)) ?? [];
});

export const cartInfoSelector = createSelector(currencySelector, shoppingCartSelector, (currency, cart) => {
    let surcharges = 0;
    let deliveryCosts = 0;
    let itemsInCart = 0;
    let total = 0;
    const functionalAssets: (FunctionalAsset | null)[] = [];

    cart?.suppliers.forEach(supplier => {
        itemsInCart += supplier.items.length;
        surcharges += supplier.surcharges;
        deliveryCosts += supplier.deliveryCosts;
        total = total + supplier.netTotal + supplier.deliveryCosts + supplier.surcharges;
        if (supplier.requireFunctionalAssets) {
            functionalAssets[supplier.id] = supplier.functionalAsset ?? null;
        }
    });

    return {
        surcharges,
        deliveryCosts,
        total,
        itemsInCart,
        currency,
        functionalAssets,
    };
});

export const shoppingCartMinimalInfoSelector = createSelector(currencySelector, shoppingCartState, (currency, cart) => {
    return {
        totalNetPrice: cart.subtotal,
        itemCount: cart.itemCount,
        currency,
    };
});

export const orderDataSelector = createSelector(
    deliveryOptionSelector,
    shoppingCartMinimalInfoSelector,
    shoppingCartState,
    (options, cartInfo, state) => ({
        selectedDeliveryAddressId: state.shoppingCart?.deliveryAddress?.id ?? null,
        selectedInvoiceAddressId: state.shoppingCart?.invoiceAddress?.id ?? null,
        selectedAvisoContactId: state.shoppingCart?.avisoContact?.id ?? -1,
        desiredDeliveryDate:
            (state.shoppingCart?.desiredDeliveryDate && dayjs(state.shoppingCart.desiredDeliveryDate)) ||
            (options?.desiredDeliveryDate && dayjs(options.desiredDeliveryDate)) ||
            null,
        orderReference: state.shoppingCart?.orderReference ?? '',
        costCentre: state.shoppingCart?.costCentre ?? state.shoppingCart?.deliveryAddress?.costCentre ?? '',
        hasOrderLimit:
            (state.shoppingCart?.monthlyOrderLimit !== undefined &&
                (state.shoppingCart.currentMonthlyOrderSum ?? 0) + cartInfo.totalNetPrice >
                    state.shoppingCart.monthlyOrderLimit) ||
            (state.shoppingCart?.perOrderOrderLimit !== undefined &&
                cartInfo.totalNetPrice > state.shoppingCart.perOrderOrderLimit),
    })
);
